import { createLogger } from '../utils/Logger';

export interface PlayerStatsConfig {
    moveSpeed: number;
    pickupRange: number;
    maxHealth: number;
    damageMultiplier: number;
    cooldownMultiplier: number;
}

export interface ActivePassiveStatsSnapshot {
    id: string;
    level: number;
    modifiers?: Partial<PlayerStatsConfig>;
}

export class PlayerStats {
    private stats: PlayerStatsConfig;
    private baseStats: PlayerStatsConfig;
    private readonly logger = createLogger('PlayerStats');

    constructor(config: Partial<PlayerStatsConfig> = {}) {
        this.baseStats = {
            moveSpeed: 160,
            pickupRange: 100,
            maxHealth: 100,
            damageMultiplier: 1.0,
            cooldownMultiplier: 1.0,
            ...config
        };
        this.stats = { ...this.baseStats };
    }

    public get(key: keyof PlayerStatsConfig): number {
        return this.stats[key];
    }

    public set(key: keyof PlayerStatsConfig, value: number): void {
        this.stats[key] = value;
        this.baseStats[key] = value;
    }

    public modify(key: keyof PlayerStatsConfig, delta: number): void {
        this.stats[key] += delta;
        this.baseStats[key] += delta;
    }

    public multiply(key: keyof PlayerStatsConfig, factor: number): void {
        this.stats[key] *= factor;
        this.baseStats[key] *= factor;
    }

    public recalculate(passives: ReadonlyArray<ActivePassiveStatsSnapshot>): void {
        this.logger.debug('Recalculate START', {
            passives: passives.map(passive => `${passive.id}:${passive.level}`)
        });

        const nextStats: PlayerStatsConfig = { ...this.baseStats };

        passives.forEach(passive => {
            if (!passive.modifiers) {
                this.logger.warn('Skipping unknown passive level during recalculation', passive);
                return;
            }

            Object.entries(passive.modifiers).forEach(([key, value]) => {
                if (typeof value !== 'number') return;
                nextStats[key as keyof PlayerStatsConfig] = value;
            });
        });

        this.stats = nextStats;
        this.logger.debug('Recalculate END', { stats: this.getSnapshot() });
    }

    public getSnapshot(): PlayerStatsConfig {
        return { ...this.stats };
    }
}
